import { type Button, type Fabric, type SceneEntity, scene, sceneEntities } from '@seddi/scene';
import type { SurfaceMaterial } from '@seddi/surface-material';

import type { LogoDesc } from './logoTypes';
import type { SceneDesc } from './sceneTypes';

/**
 * Find button resource.
 * @param self - Scene descriptor.
 * @param id - Button ID.
 */
export const findButton = (self: SceneDesc, id: string): Button | undefined => {
  return self.buttons.find((b) => b.id === id);
};

/**
 * Find fabric resource.
 * @param self - Scene descriptor.
 * @param id - Button ID.
 */
export const findFabric = (self: SceneDesc, id: string): Fabric | undefined => {
  return self.fabrics.find((f) => f.id === id);
};

/**
 * Find logo resource.
 * @param self - Scene descriptor.
 * @param id - Button ID.
 */
export const findLogo = (self: SceneDesc, id: string): LogoDesc | undefined => {
  return self.logos.find((l) => l.id === id);
};

/**
 * Find material resource.
 * @param self - Scene descriptor.
 * @param id - Material ID.
 */
export const findMaterial = (self: SceneDesc, id: string): SurfaceMaterial | undefined => {
  return self.materials.find((m) => m.id === id);
};

/**
 * Find entity with camera component.
 * @param self - Scene descriptor.
 */
export const findWithCamera = (self: SceneDesc): SceneEntity | undefined => {
  return self.children.find((e) => e.components.camera);
};

/**
 * Find entity with environment component.
 * @param self - Scene descriptor.
 */
export const findWithEnvironment = (self: SceneDesc): SceneEntity | undefined => {
  return self.children.find((e) => e.components.environment);
};

/**
 * Find entity with garment component.
 * @param self - Scene descriptor.
 */
export const findWithGarment = (self: SceneDesc): SceneEntity | undefined => {
  return self.children.find((e) => e.components.garment);
};

/**
 * Filter entities with mesh component.
 * @param self - Scene descriptor.
 */
export const filterWithMesh = (self: SceneDesc): SceneEntity[] => {
  return self.children.filter((e) => e.components.mesh);
};

/**
 * Filter entities with model components.
 * @param self - Scene descriptor.
 */
export const filterWithModel = (self: SceneDesc): SceneEntity[] => {
  return self.children.filter((e) => e.components.model);
};

/**
 * Filter entities with piece component.
 * @param self - Scene descriptor.
 */
export const filterWithPiece = (self: SceneDesc): SceneEntity[] => {
  return self.children.filter((e) => e.components.garmentPiece);
};

/**
 * Filter entities with seam component.
 * @param self - Scene descriptor.
 */
export const filterWithSeam = (self: SceneDesc): SceneEntity[] => {
  return self.children.filter((e) => e.components.garmentSeam);
};

/**
 * Filter entities with seam component and tape.
 * @param self - Scene descriptor.
 */
export const filterWithSeamTape = (self: SceneDesc): SceneEntity[] => {
  return self.children.filter((e) => e.components.garmentSeam?.tape);
};

export function getGarmentGeometryId(desc: SceneDesc): string | undefined {
  // TODO: Use surface URL filepath as ID?.
  return findWithGarment(desc)?._id;
}

export function getGarmentOcclusionId(desc: SceneDesc): string | undefined {
  const entity = findWithGarment(desc);
  return entity.components.garment!.occlusion?.id;
}

// ------ SYNTAX OVERRIDES ------

export const filterEntitiesWithParent = sceneEntities.FilterByParent as (
  scene: SceneEntity[],
  id: string,
) => SceneEntity[];

export const findRoot = scene.FindRoot as (scene: SceneDesc) => SceneEntity;
